/**
 * I18n Implementation
 *
 * Simple i18n implementation with support for:
 * - Multiple locales
 * - String interpolation with {{variable}} syntax
 * - Fallback locale support
 *
 * This implementation can be swapped for a library like i18next
 * without changing consumer code.
 */

import type {
  I18nInterface,
  I18nConfigType,
  TranslationParamsType,
  TranslationsByLocaleType,
} from "../types/i18n.interface";
import type {
  TranslationArgs,
  TranslationKey,
} from "../types/translation-key.types.js";
import type { KnownLocale } from "../schemas/locale.schemas.js";

export class I18n<L extends KnownLocale> implements I18nInterface<L> {
  private locale: L;
  private readonly fallbackLocale: L;
  private readonly translations: TranslationsByLocaleType;
  private debug: boolean;

  constructor(config: I18nConfigType<L>) {
    this.locale = config.defaultLocale;
    this.fallbackLocale = config.fallbackLocale ?? config.defaultLocale;
    this.translations = config.translations;
    this.debug = config.debug ?? false;
  }

  t<K extends TranslationKey>(key: K, ...args: TranslationArgs<K>): string;
  t(key: TranslationKey, params?: TranslationParamsType): string {
    // Try current locale first, then fallback
    let translation = this.translations[this.locale]?.[key];

    if (translation === undefined && this.locale !== this.fallbackLocale) {
      translation = this.translations[this.fallbackLocale]?.[key];
    }

    // Return key with warning indicator if no translation found
    if (translation === undefined) {
      return `⚠️${key}⚠️`;
    }

    let result = translation;
    if (params !== undefined) {
      result = this.interpolate(result, params);
    }

    if (this.debug) {
      return `[${result}]`;
    }

    return result;
  }

  setLocale(locale: L): void {
    this.locale = locale;
  }

  getLocale(): L {
    return this.locale;
  }

  hasTranslation(key: TranslationKey): boolean {
    return (
      this.translations[this.locale]?.[key] !== undefined ||
      this.translations[this.fallbackLocale]?.[key] !== undefined
    );
  }

  setDebug(enabled: boolean): void {
    this.debug = enabled;
  }

  isDebug(): boolean {
    return this.debug;
  }

  private interpolate(template: string, params: TranslationParamsType): string {
    let result = template;

    for (const [key, value] of Object.entries(params)) {
      result = result.replace(
        new RegExp(`\\{\\{${key}\\}\\}`, "g"),
        String(value),
      );
    }

    return result;
  }
}
